<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-gray-700">
              <!-- Header -->
              <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <DialogTitle as="h3" class="text-xl font-semibold text-gray-900 dark:text-white">
                  {{ mode === 'signin' ? 'Sign In' : 'Create Account' }}
                </DialogTitle>
                <button
                  type="button"
                  class="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                  @click="closeModal"
                >
                  <XMarkIcon class="h-5 w-5" />
                </button>
              </div>

              <!-- Content -->
              <div class="p-6 space-y-6">
                <!-- Error message -->
                <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
                  <div class="flex">
                    <ExclamationTriangleIcon class="h-5 w-5 text-red-400 flex-shrink-0" />
                    <div class="ml-3">
                      <p class="text-sm text-red-700 dark:text-red-400">{{ error }}</p>
                    </div>
                  </div>
                </div>

                <!-- Success message -->
                <div v-if="success" class="rounded-xl bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
                  <div class="flex">
                    <CheckCircleIcon class="h-5 w-5 text-green-400 flex-shrink-0" />
                    <div class="ml-3">
                      <p class="text-sm text-green-700 dark:text-green-400">{{ success }}</p>
                    </div>
                  </div>
                </div>

                <!-- Google Sign In Button -->
                <button
                  type="button"
                  class="w-full flex justify-center items-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                  @click="signInWithGoogle"
                  :disabled="loading"
                >
                  <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {{ loading ? 'Signing in...' : 'Continue with Google' }}
                </button>

                <!-- Divider -->
                <div class="relative">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-300 dark:border-gray-600" />
                  </div>
                  <div class="relative flex justify-center text-sm">
                    <span class="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with email</span>
                  </div>
                </div>

                <!-- Email/Password Form -->
                <form @submit.prevent="handleEmailAuth" class="space-y-4">
                  <!-- Display Name (only for sign up) -->
                  <div v-if="mode === 'signup'" class="space-y-2">
                    <label for="displayName" class="block text-sm font-semibold text-gray-900 dark:text-white">
                      Display Name
                    </label>
                    <div class="relative">
                      <input
                        id="displayName"
                        v-model="form.displayName"
                        type="text"
                        class="block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm"
                        placeholder="Your display name"
                      />
                    </div>
                  </div>

                  <!-- Email -->
                  <div class="space-y-2">
                    <label for="email" class="block text-sm font-semibold text-gray-900 dark:text-white">
                      Email Address
                    </label>
                    <div class="relative">
                      <input
                        id="email"
                        v-model="form.email"
                        type="email"
                        required
                        class="block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <!-- Password -->
                  <div class="space-y-2">
                    <label for="password" class="block text-sm font-semibold text-gray-900 dark:text-white">
                      Password
                    </label>
                    <div class="relative">
                      <input
                        id="password"
                        v-model="form.password"
                        type="password"
                        required
                        class="block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm"
                        placeholder="••••••••"
                        :minlength="mode === 'signup' ? 6 : undefined"
                      />
                    </div>
                  </div>

                  <!-- Submit Button -->
                  <button
                    type="submit"
                    class="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    :disabled="loading"
                  >
                    <div v-if="loading" class="flex items-center">
                      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Please wait...
                    </div>
                    <span v-else>{{ mode === 'signin' ? 'Sign In' : 'Create Account' }}</span>
                  </button>
                </form>

                <!-- Mode Toggle -->
                <div class="text-center">
                  <button
                    type="button"
                    class="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 font-medium transition-colors"
                    @click="toggleMode"
                  >
                    {{ mode === 'signin' ? "Don't have an account? Sign up" : "Already have an account? Sign in" }}
                  </button>
                </div>

                <!-- Forgot Password -->
                <div v-if="mode === 'signin'" class="text-center">
                  <button
                    type="button"
                    class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    @click="showForgotPassword"
                  >
                    Forgot your password?
                  </button>
                </div>

                <!-- Forgot Password Form -->
                <div v-if="showForgotPasswordForm" class="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Reset Password</h4>
                  <form @submit.prevent="handleForgotPassword" class="space-y-3">
                    <div class="relative">
                      <input
                        v-model="forgotPasswordEmail"
                        type="email"
                        required
                        class="block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm"
                        placeholder="Enter your email address"
                      />
                    </div>
                    <div class="flex gap-2">
                      <button
                        type="submit"
                        class="flex-1 inline-flex justify-center items-center px-4 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-all duration-200"
                        :disabled="loading"
                      >
                        Send Reset Email
                      </button>
                      <button
                        type="button"
                        class="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                        @click="showForgotPasswordForm = false"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>

                <!-- Anonymous Sign In -->
                <div class="pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    type="button"
                    class="w-full flex justify-center items-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                    @click="browseAsGuest"
                    :disabled="loading"
                  >
                    Continue without an account
                  </button>
                  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Browse wallpapers without creating a Firebase Auth account
                  </p>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { authService, type AuthUser } from '../firebase/services/authService';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success', user: AuthUser): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

// Component state
const mode = ref<'signin' | 'signup'>('signin');
const loading = ref(false);
const error = ref('');
const success = ref('');
const showForgotPasswordForm = ref(false);
const forgotPasswordEmail = ref('');

// Form data
const form = reactive({
  email: '',
  password: '',
  displayName: ''
});

// Methods
const closeModal = () => {
  resetForm();
  emit('close');
};

const resetForm = () => {
  form.email = '';
  form.password = '';
  form.displayName = '';
  error.value = '';
  success.value = '';
  showForgotPasswordForm.value = false;
  forgotPasswordEmail.value = '';
  mode.value = 'signin';
};

const toggleMode = () => {
  mode.value = mode.value === 'signin' ? 'signup' : 'signin';
  error.value = '';
  success.value = '';
};

const showForgotPassword = () => {
  showForgotPasswordForm.value = true;
  forgotPasswordEmail.value = form.email;
};

const handleEmailAuth = async () => {
  if (loading.value) return;
  
  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    let user;
    if (mode.value === 'signin') {
      user = await authService.signInWithEmail(form.email, form.password);
    } else {
      user = await authService.createAccountWithEmail(
        form.email, 
        form.password, 
        form.displayName
      );
    }
    
    success.value = mode.value === 'signin' ? 'Signed in successfully!' : 'Account created successfully!';
    emit('success', user);
    
    // Close modal after a short delay
    setTimeout(() => {
      closeModal();
    }, 1000);
    
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
};

const signInWithGoogle = async () => {
  if (loading.value) return;
  
  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    const user = await authService.signInWithGoogle();
    success.value = 'Signed in with Google successfully!';
    emit('success', user);
    
    // Close modal after a short delay
    setTimeout(() => {
      closeModal();
    }, 1000);
    
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
};

const browseAsGuest = () => {
  if (loading.value) return;

  error.value = '';
  success.value = 'Continuing without an account';

  // Close modal after a short delay so the user can keep browsing without Firebase Auth.
  setTimeout(() => {
    closeModal();
  }, 600);
};

const handleForgotPassword = async () => {
  if (loading.value || !forgotPasswordEmail.value) return;
  
  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    await authService.sendPasswordResetEmail(forgotPasswordEmail.value);
    success.value = 'Password reset email sent! Check your inbox.';
    showForgotPasswordForm.value = false;
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
};
</script>